/**
 * Thin ergonomic wrappers over Neon design-system web components so the rest of
 * the app uses plain `value`/`onChange` props instead of Neon's
 * `modelValue`/`modelValueChange` + `data*` attribute conventions.
 */
import {
  NeonButton,
  NeonTextField,
  NeonNumberField,
  NeonSelectField,
  NeonCheckboxSingle,
  NeonTag,
  type SelectOption,
} from '@ps-refarch-ux/neon';

const boolAttr = (v?: boolean): 'true' | undefined => (v ? 'true' : undefined);

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'utility' | 'borderless';

export interface ButtonProps {
  id: string;
  text: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  icon?: string;
  iconPosition?: 'left' | 'right';
  submit?: boolean;
  loading?: boolean;
  disabled?: boolean;
  ariaHaspopup?: string;
  className?: string;
  href?: string;
  openInNewTab?: boolean;
}

export function Button({
  id,
  text,
  onClick,
  variant = 'primary',
  icon,
  iconPosition,
  submit,
  loading,
  disabled,
  ariaHaspopup,
  className,
  href,
  openInNewTab,
}: ButtonProps) {
  return (
    <NeonButton
      id={id}
      className={className}
      dataText={text}
      dataType={variant}
      dataIcon={icon}
      dataIconPosition={iconPosition}
      dataIsSubmit={boolAttr(submit)}
      dataIsLoading={boolAttr(loading)}
      dataAriaHaspopup={ariaHaspopup}
      disabled={boolAttr(disabled)}
      href={href}
      dataOpenInNewTab={boolAttr(openInNewTab)}
      onClick={onClick ? () => onClick() : undefined}
    />
  );
}

export interface TextInputProps {
  id: string;
  label: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  email?: boolean;
  helper?: string;
  icon?: string;
  clearable?: boolean;
}

export function TextInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  disabled,
  readOnly,
  email,
  helper,
  icon,
  clearable,
}: TextInputProps) {
  return (
    <NeonTextField
      id={id}
      dataLabelText={label}
      modelValue={value ?? ''}
      modelValueChange={(v) => onChange(v ?? '')}
      dataPlaceholderText={placeholder}
      dataIsRequired={boolAttr(required)}
      disabled={boolAttr(disabled)}
      dataIsReadOnly={boolAttr(readOnly)}
      dataIsEmail={boolAttr(email)}
      dataHelperText={helper}
      dataIcon={icon}
      dataIsClearable={boolAttr(clearable)}
    />
  );
}

export interface NumberInputProps {
  id: string;
  label: string;
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
  integer?: boolean;
  min?: number;
  max?: number;
}

export function NumberInput({ id, label, value, onChange, disabled, integer, min, max }: NumberInputProps) {
  return (
    <NeonNumberField
      id={id}
      dataLabelText={label}
      modelValue={value ?? undefined}
      modelValueChange={(v) => onChange(v)}
      disabled={boolAttr(disabled)}
      dataIsInteger={boolAttr(integer)}
      dataMinValue={min}
      dataMaxValue={max}
    />
  );
}

export interface SelectInputProps {
  id: string;
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  options: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyText?: string;
  disabled?: boolean;
  showFilter?: boolean;
}

export function SelectInput({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  allowEmpty,
  emptyText,
  disabled,
  showFilter,
}: SelectInputProps) {
  const neonOptions: SelectOption[] = options.map((o) => ({ text: o.label, value: String(o.value) }));
  return (
    <NeonSelectField
      id={id}
      dataLabelText={label}
      modelValue={value == null || value === '' ? undefined : String(value)}
      modelValueChange={(v) => onChange(v ?? '')}
      options={neonOptions}
      dataPlaceholderText={placeholder}
      dataUndefinedOption={boolAttr(allowEmpty)}
      dataUndefinedOptionText={emptyText ?? placeholder ?? '— none —'}
      disabled={boolAttr(disabled)}
      dataShowFilter={boolAttr(showFilter)}
    />
  );
}

export interface CheckboxInputProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  toggle?: boolean;
  disabled?: boolean;
}

export function CheckboxInput({ id, label, checked, onChange, toggle, disabled }: CheckboxInputProps) {
  return (
    <NeonCheckboxSingle
      id={id}
      dataLabelText={label}
      value={checked}
      dataCheckType={toggle ? 'toggle' : 'check'}
      disabled={boolAttr(disabled)}
      onInput={(e: unknown) => onChange((e as { target: { checked: boolean } }).target.checked)}
    />
  );
}

const STATUS_COLOR: Record<string, string> = {
  Active: 'green',
  None: 'gray',
  Past: 'orange',
  Demo: 'purple',
};

export function StatusTag({ status }: { status: string }) {
  return <NeonTag dataText={status} dataType={STATUS_COLOR[status] ?? 'gray'} />;
}
